import { t } from '@/i18n';
import {
  type AmountUnit,
  AmountUnitLabelModifier,
  type PaperSize,
} from '@/utils';
import {
  type ComponentProps,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  isValidElement,
} from 'react';
import { Flex, type FlexProps, Text } from '..';

type PageOrientation = 'portrait' | 'landscape';
export const PAPER_FILE_SIZE = {
  A4: {
    portrait: { width: '210mm', height: '297mm' },
    landscape: { width: '297mm', height: '210mm' },
  },
  A3: {
    portrait: { width: '297mm', height: '420mm' },
    landscape: { width: '420mm', height: '297mm' },
  },
};

type Props = {
  title?: ComponentProps<typeof Text>;
  headerStyle?: FlexProps['style'];
  layout?: { paperSize: PaperSize; orientation: PageOrientation };
  caption?: {
    item?: ReactNode;
    wrapperStyle?: FlexProps['style'];
    unit?: AmountUnit;
  };
  pagePadding?: number;
  dateByCreated?: string;
};

export const ReportPage: FC<PropsWithChildren<Props>> = ({
  title,
  layout,
  children,
  caption,
  pagePadding,
  headerStyle,
  dateByCreated,
}) => {
  return (
    <Flex
      vertical
      style={{
        width: layout
          ? PAPER_FILE_SIZE[layout.paperSize][layout.orientation].width
          : undefined,
        printColorAdjust: 'exact',
        WebkitPrintColorAdjust: 'exact',
        padding: pagePadding,
      }}
    >
      <Flex vertical style={{ ...headerStyle }}>
        {dateByCreated && (
          <Flex justify="flex-end">
            <Text weight="regular" variant="small" text={dateByCreated} />
          </Flex>
        )}
        {title && <Text weight="bold" {...title} />}
        {caption && (
          <Flex
            style={{
              justifyContent: caption.item ? 'space-between' : 'flex-end',
              alignItems: 'center',
              ...caption.wrapperStyle,
            }}
          >
            {caption.item &&
              (isValidElement(caption.item) ? (
                caption.item
              ) : (
                <Text weight="regular" text={caption.item as string | number} />
              ))}
            {caption.unit && (
              <Text
                weight="regular"
                variant="small"
                text={t('report-amount-by-unit', {
                  unit: AmountUnitLabelModifier[caption.unit],
                })}
              />
            )}
          </Flex>
        )}
      </Flex>

      {children}
    </Flex>
  );
};
